import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { money } from "../lib/api";
import { BUCKETS, categorizeFee, isFinancedFee } from "../lib/concessions";
import { ShieldCheck } from "lucide-react";

const num = (v) => Number(v || 0);

export function FeeBreakdownModal({ open, onClose, client, maxPct, maxAmount }) {
  const costs = (client?.closing_costs || []).map((c) => ({
    label: c.label || "Fee",
    amount: num(c.amount),
    // VA funding fee / FHA upfront MIP are financed into the loan — hard rule, overrides any stored category.
    category: isFinancedFee(c.label) ? "financed" : c.category || categorizeFee(c.label),
  }));
  const prepaids = (client?.prepaids_items || []).map((c) => ({ label: c.label || "Prepaid", amount: num(c.amount) }));
  const financed = costs.filter((c) => c.category === "financed");

  const itemsFor = (key) => (key === "prepaids" ? prepaids : costs.filter((c) => c.category === key));
  const subtotal = (key) => itemsFor(key).reduce((s, c) => s + c.amount, 0);
  const grandTotal = BUCKETS.reduce((s, b) => s + subtotal(b.key), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white max-w-lg w-[96vw] max-h-[92vh] overflow-y-auto"
        data-testid="fee-breakdown-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Loan estimate — fee breakdown</DialogTitle>
          <DialogDescription className="text-white/55">
            A preview of the loan estimate you're building. Use the total to know how much in seller concessions to ask for.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {BUCKETS.map((b) => {
            const items = itemsFor(b.key);
            const sub = subtotal(b.key);
            return (
              <div
                key={b.key}
                data-testid={`bucket-${b.key}`}
                className={
                  b.highlight
                    ? "rounded-xl border-2 border-[#ff3b30]/60 bg-[#ff3b30]/10 p-4"
                    : "rounded-xl border border-white/10 bg-white/[0.03] p-4"
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {b.highlight && <ShieldCheck className="h-4 w-4 text-[#ff3b30] shrink-0" />}
                    <p className={`font-semibold ${b.highlight ? "text-[#ff8a82]" : "text-white"}`}>{b.label}</p>
                  </div>
                  <span className="font-display text-lg tracking-tight mpl-tabular" data-testid={`bucket-total-${b.key}`}>
                    {money(sub)}
                  </span>
                </div>
                <p className="text-[11px] text-white/40 mt-0.5">{b.hint}</p>
                {b.highlight && (
                  <p className="text-[11px] text-[#ff8a82]/90 mt-1 font-medium">
                    This is all your lender charges — every other line below is a normal, third-party cost.
                  </p>
                )}
                <div className="mt-2 space-y-1">
                  {items.length === 0 ? (
                    <p className="text-xs text-white/30">None on this loan.</p>
                  ) : (
                    items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-white/60 truncate pr-3">{it.label}</span>
                        <span className="text-white/80 mpl-tabular">{money(it.amount)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}

          <div className="rounded-xl border border-white/15 bg-black/40 p-4 flex items-center justify-between">
            <p className="font-semibold">Total closing costs to cover</p>
            <span className="font-display text-2xl tracking-tight text-[#ff3b30] mpl-tabular" data-testid="fee-grand-total">
              {money(grandTotal)}
            </span>
          </div>

          {financed.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="financed-note">
              <p className="text-[11px] uppercase tracking-[0.14em] text-white/40 mb-2">Financed into the loan · not cash to close</p>
              {financed.map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/45 truncate pr-3">{it.label}</span>
                  <span className="text-white/55 mpl-tabular">{money(it.amount)}</span>
                </div>
              ))}
              <p className="text-[11px] text-white/35 mt-2">Rolled into the loan amount — the buyer doesn't bring this to closing, so it's not part of the total above.</p>
            </div>
          )}

          {maxAmount != null && (
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-emerald-300">Max seller concessions you can request</p>
                <p className="text-[11px] text-white/45">{maxPct}% of the offer price for this loan program</p>
              </div>
              <span className="font-display text-2xl tracking-tight text-emerald-300 mpl-tabular" data-testid="fee-max-concession">
                {money(maxAmount)}
              </span>
            </div>
          )}

          <p className="text-[11px] text-white/40 leading-relaxed pt-1" data-testid="modal-concession-disclaimer">
            <span className="text-white/55 font-semibold">Note:</span> Seller concessions cannot exceed the buyer's total closing costs — the buyer can't receive cash back at closing.
          </p>
        </div>

        <div className="flex justify-end mt-2">
          <Button
            onClick={onClose}
            data-testid="fee-breakdown-close"
            className="h-11 bg-white text-black hover:bg-white/90 font-semibold rounded-xl px-6"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
